/**
 * Audio Attachment Utilities
 *
 * Inbound mail is inconsistent about content types: a call recording can
 * arrive as `audio/mpeg`, `audio/mp3` or `application/octet-stream`. The
 * filename extension is the fallback when the stored content type says nothing.
 */

const AUDIO_MIME_BY_EXTENSION: Record<string, string> = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  opus: 'audio/ogg',
  flac: 'audio/flac',
  weba: 'audio/webm',
};

interface AudioAttachmentLike {
  readonly docName?: string;
  readonly docContentType?: string;
}

const extensionOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? '' : fileName.slice(dot + 1).toLowerCase();
};

/**
 * The MIME type to play an attachment with, or `null` when it is not audio.
 * A stored `audio/*` type wins; otherwise the extension decides.
 */
export const audioMimeType = ({
  docName = '',
  docContentType = '',
}: AudioAttachmentLike): string | null => {
  if (docContentType.toLowerCase().startsWith('audio/')) {
    return docContentType;
  }
  return AUDIO_MIME_BY_EXTENSION[extensionOf(docName)] ?? null;
};

export const isAudioAttachment = (attachment: AudioAttachmentLike): boolean =>
  audioMimeType(attachment) !== null;

/** `m:ss`, or `h:mm:ss` past an hour. Unknown or invalid input reads `0:00`. */
export const formatPlaybackTime = (seconds: number): string => {
  const total =
    Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, '0');

  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${secs}`
    : `${minutes}:${secs}`;
};

/**
 * Fetch a recording's transcript. `null` means there is none yet: S3 answers
 * 403 or 404 for a key data-extractor has not written.
 */
export const fetchAttachmentTranscript = async (
  url: string
): Promise<string | null> => {
  const response = await fetch(url);
  if (response.status === 403 || response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`The file store returned ${response.status}.`);
  }
  return (await response.text()).trim() || null;
};
